import { Injectable } from '@angular/core';
import { Product, getDdqIssuerUidFromProduct, getDdqProductUidFromProduct } from '@core/models/product.model';

export interface ClientDdqApiScopeSnapshot {
  routeProductId: string;
  issuerUid: string;
  ddqProductUid: string | null;
}

@Injectable({ providedIn: 'root' })
export class ClientDdqScopeCacheService {
  private readonly scopesByRouteProductId = new Map<string, ClientDdqApiScopeSnapshot>();

  public upsertFromProduct(product: Product | null | undefined, routeProductId?: string | null): void {
    const issuerUid = getDdqIssuerUidFromProduct(product);
    const resolvedRouteProductId = this.normalize(routeProductId ?? product?.uid ?? product?.id ?? null);
    if (!issuerUid || !resolvedRouteProductId) {
      return;
    }
    const ddqProductUid = this.normalize(
      getDdqProductUidFromProduct(product) ?? product?.ddqSummary?.productUid ?? product?.uid ?? product?.id ?? null,
    );
    this.putSnapshot({
      routeProductId: resolvedRouteProductId,
      issuerUid,
      ddqProductUid,
    });
  }

  public upsert(routeProductId: string | null | undefined, issuerUid: string | null | undefined, ddqProductUid?: string | null): void {
    const normalizedRouteProductId = this.normalize(routeProductId);
    const normalizedIssuerUid = this.normalize(issuerUid);
    if (!normalizedRouteProductId || !normalizedIssuerUid) {
      return;
    }
    this.putSnapshot({
      routeProductId: normalizedRouteProductId,
      issuerUid: normalizedIssuerUid,
      ddqProductUid: this.normalize(ddqProductUid ?? null),
    });
  }

  public getSnapshotForRouteProduct(routeProductId: string | null | undefined): ClientDdqApiScopeSnapshot | null {
    const key = this.normalize(routeProductId);
    return key ? this.scopesByRouteProductId.get(key) ?? null : null;
  }

  /**
   * Basket items (and client DDQ navigation) may key the URL by marketplace `product.id` while
   * `navigateToDdq` can land on `/client/{ddqProductUid}/ddq` — register both so URL builders
   * and `OrganizationContextService` resolve issuer scope after `NavigationEnd`.
   */
  private putSnapshot(snapshot: ClientDdqApiScopeSnapshot): void {
    this.scopesByRouteProductId.set(snapshot.routeProductId, snapshot);
    const alias = this.normalize(snapshot.ddqProductUid);
    if (alias && alias !== snapshot.routeProductId) {
      this.scopesByRouteProductId.set(alias, snapshot);
    }
  }

  private normalize(value: string | null | undefined): string | null {
    if (value == null) {
      return null;
    }
    const normalized = String(value).trim();
    return normalized === '' ? null : normalized;
  }
}
